/**
 * Compliance Dashboard with Vault UI patterns
 *
 * Ticker: recent ModComments + Emails (live communication feed)
 * Stat cards: Policies, Content, AML Pending, Open Tasks
 * Carousel: recent Policies + Content sorted by createdtime
 * Sections: Policies & Content, Projects, AML
 */

import React, { useState, useMemo } from "react";
import { Row, Col, Card, Statistic } from "antd";
import {
  FileProtectOutlined,
  ReadOutlined,
  ReconciliationOutlined,
  CheckSquareOutlined,
} from "@ant-design/icons";
import { useList } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import {
  TickerBanner,
  FeatureCarousel,
  SectionedList,
  UtilityBar,
} from "@/components/vault";
import type { TickerItem, CarouselCard, Section } from "@/components/vault";
import { resolveCommentAuthor, stripContactPrefix } from "@/utils/commentAuthor";
import { timeAgo, formatDate } from "@/utils/timeAgo";

// ─── Helpers ─────────────────────────────────────────────────────────

/** Safely stringify Refine's BaseKey */
const sid = (id: unknown): string => String(id ?? "");

/** Extract first recipient from Emails.saved_toid (JSON array or plain string) */
function firstRecipient(savedToid: string | undefined): string {
  if (!savedToid) return "";
  try {
    const parsed = JSON.parse(savedToid);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed[0];
  } catch {
    /* not JSON — use as-is */
  }
  return savedToid.split(",")[0]?.trim() || "";
}

// ─── Dashboard ───────────────────────────────────────────────────────

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  // ─── Data hooks ────────────────────────────────────────────────────

  const { data: policiesData } = useList({
    resource: "policies",
    pagination: { pageSize: 50 },
    sorters: [{ field: "createdtime", order: "desc" }],
  });

  const { data: contentData } = useList({
    resource: "content",
    pagination: { pageSize: 50 },
    sorters: [{ field: "createdtime", order: "desc" }],
  });

  const { data: amlData } = useList({
    resource: "anti-money-laundering",
    pagination: { pageSize: 50 },
    sorters: [{ field: "subject", order: "asc" }],
  });

  const { data: projectsData } = useList({
    resource: "projects",
    pagination: { pageSize: 50 },
    sorters: [{ field: "startdate", order: "desc" }],
  });

  const { data: tasksData } = useList({
    resource: "project-tasks",
    pagination: { pageSize: 100 },
    sorters: [{ field: "enddate", order: "asc" }],
  });

  const { data: commentsData } = useList({
    resource: "comments",
    pagination: { pageSize: 10 },
    sorters: [{ field: "createdtime", order: "desc" }],
  });

  const { data: emailsData } = useList({
    resource: "emails",
    pagination: { pageSize: 10 },
    sorters: [{ field: "createdtime", order: "desc" }],
  });

  const policies = policiesData?.data || [];
  const content = contentData?.data || [];
  const aml = amlData?.data || [];
  const projects = projectsData?.data || [];
  const tasks = tasksData?.data || [];
  const comments = commentsData?.data || [];
  const emails = emailsData?.data || [];

  // ─── Metrics ─────────────────────────────────────────────────────

  const metrics = useMemo(
    () => ({
      totalPolicies: policies.length,
      totalContent: content.length,
      amlPending: aml.filter(
        (a) => !a.data_verifica || a.data_verifica === ""
      ).length,
      openTasks: tasks.filter(
        (t) =>
          t.projecttaskstatus !== "Completed" &&
          t.projecttaskstatus !== "Deferred"
      ).length,
    }),
    [policies, content, aml, tasks]
  );

  // ─── Ticker items (new/unread comments + emails) ─────────────────

  const tickerItems: TickerItem[] = useMemo(() => {
    // Only show items newer than the last time the user dismissed the ticker
    const lastSeen = localStorage.getItem("ticker_last_seen") || "";

    const items: { id: string; text: string; date: string; onClick?: () => void }[] = [];

    comments
      .filter((c) => {
        const ct = String(c.createdtime || "");
        return !lastSeen || ct > lastSeen;
      })
      .forEach((c) => {
        const author = resolveCommentAuthor(c as any);
        const snippet = stripContactPrefix(String(c.commentcontent || "")).slice(0, 60);
        const name = author.name || "CRM";
        const relatedTo = String(c.related_to || "");
        items.push({
          id: `comment-${c.id}`,
          text: `${name}: ${snippet} \u00b7 ${timeAgo(String(c.createdtime || ""))}`,
          date: String(c.createdtime || ""),
          onClick: relatedTo
            ? () => navigate(`/messages/${relatedTo}`)
            : undefined,
        });
      });

    emails
      .filter((e) => {
        // Only show emails not yet opened
        const flag = String(e.email_flag || "").toLowerCase();
        return flag !== "opened" && flag !== "read";
      })
      .forEach((e) => {
        const subject = String(e.subject || "(senza oggetto)");
        const recipient = firstRecipient(String(e.saved_toid || ""));
        items.push({
          id: `email-${e.id}`,
          text: `${subject} \u2192 ${recipient || "?"} \u00b7 ${timeAgo(String(e.createdtime || ""))}`,
          date: String(e.createdtime || ""),
          onClick: () => navigate("/emails"),
        });
      });

    // Interleave by date, most recent first
    items.sort((a, b) => (b.date > a.date ? 1 : -1));
    return items.slice(0, 10).map(({ id, text, onClick }) => ({ id, text, onClick }));
  }, [comments, emails, navigate]);

  // ─── Carousel cards (Policies + Content by createdtime) ──────────

  const carouselCards: CarouselCard[] = useMemo(() => {
    const all: (CarouselCard & { _sort: string })[] = [
      ...policies.map((p) => ({
        id: String(p.id),
        category: "Policy",
        timeAgo: timeAgo(String(p.createdtime || p.modifiedtime || "")),
        title: String(p.title || p.policyname || p.__entityname || "Policy"),
        muted: false,
        actionLabel: "View",
        onClick: () => navigate(`/policies/show/${p.id}`),
        onAction: () => navigate(`/policies/show/${p.id}`),
        _sort: String(p.createdtime || p.modifiedtime || ""),
      })),
      ...content.map((c) => ({
        id: String(c.id),
        category: "Contenuto",
        timeAgo: timeAgo(String(c.createdtime || c.modifiedtime || "")),
        title: String(c.title || c.__entityname || "Contenuto"),
        muted: false,
        actionLabel: "View",
        onClick: () => navigate(`/content/show/${c.id}`),
        onAction: () => navigate(`/content/show/${c.id}`),
        _sort: String(c.createdtime || c.modifiedtime || ""),
      })),
    ];

    all.sort((a, b) => (b._sort > a._sort ? 1 : -1));
    return all.slice(0, 12).map(({ _sort, ...card }) => card);
  }, [policies, content, navigate]);

  // ─── Unified timeline (last 20 records across all modules) ───────

  const sections: Section[] = useMemo(() => {
    const q = search.toLowerCase();
    const match = (text: string) => !q || text.toLowerCase().includes(q);

    const all: { row: any; sortDate: string }[] = [
      ...policies
        .filter((p) => match(String(p.title || p.policyname || p.__entityname || "")))
        .map((p) => ({
          row: {
            id: sid(p.id),
            name: String(p.title || p.policyname || p.__entityname || "Policy"),
            category: String(p.policystatus || p.status || "Draft"),
            date: formatDate(String(p.modifiedtime || p.createdtime || "")),
            description: String(p.description || ""),
            onClick: () => navigate(`/policies/show/${p.id}`),
            onAction: () => navigate(`/policies/show/${p.id}`),
          },
          sortDate: String(p.modifiedtime || p.createdtime || ""),
        })),
      ...content
        .filter((c) => match(String(c.title || c.__entityname || "")))
        .map((c) => ({
          row: {
            id: sid(c.id),
            name: String(c.title || c.__entityname || "Contenuto"),
            category: "Contenuto",
            date: formatDate(String(c.modifiedtime || c.createdtime || "")),
            description: String(c.description || ""),
            onClick: () => navigate(`/content/show/${c.id}`),
            onAction: () => navigate(`/content/show/${c.id}`),
          },
          sortDate: String(c.modifiedtime || c.createdtime || ""),
        })),
      ...projects
        .filter((p) => match(String(p.projectname || p.__entityname || "")))
        .map((p) => ({
          row: {
            id: sid(p.id),
            name: String(p.projectname || p.__entityname || "Progetto"),
            category: String(p.projectstatus || "Progetto"),
            date: formatDate(String(p.startdate || p.createdtime || "")),
            description: String(p.description || ""),
            onClick: () => navigate(`/projects/show/${p.id}`),
            onAction: () => navigate(`/projects/show/${p.id}`),
          },
          sortDate: String(p.startdate || p.createdtime || ""),
        })),
      ...aml
        .filter((a) => match(String(a.subject || a.__entityname || "")))
        .map((a) => ({
          row: {
            id: sid(a.id),
            name: String(a.subject || a.__entityname || "AML"),
            category: String(a.livello_rischio || "AML"),
            date: formatDate(String(a.data_verifica || a.createdtime || "")),
            description: String(a.description || ""),
            onClick: () => navigate(`/anti-money-laundering/show/${a.id}`),
            onAction: () => navigate(`/anti-money-laundering/show/${a.id}`),
          },
          sortDate: String(a.data_verifica || a.createdtime || ""),
        })),
    ];

    // Sort by date DESC (most recent first), take 20
    all.sort((a, b) => (b.sortDate > a.sortDate ? 1 : -1));

    return [
      {
        title: "Attività recente",
        rows: all.slice(0, 20).map((item) => item.row),
      },
    ];
  }, [policies, content, projects, aml, search, navigate]);

  // ─── Render ──────────────────────────────────────────────────────

  return (
    <div style={{ margin: "-24px", marginBottom: 0 }}>
      {/* Ticker */}
      <TickerBanner items={tickerItems} />

      {/* Metrics row — 4 cards */}
      <div style={{ padding: "20px 24px" }}>
        <Row gutter={16}>
          <Col span={6}>
            <Card size="small" hoverable onClick={() => navigate("/policies")}>
              <Statistic
                title="Policies"
                value={metrics.totalPolicies}
                prefix={<FileProtectOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" hoverable onClick={() => navigate("/content")}>
              <Statistic
                title="Contenuti"
                value={metrics.totalContent}
                prefix={<ReadOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card
              size="small"
              hoverable
              onClick={() => navigate("/anti-money-laundering")}
            >
              <Statistic
                title="AML Pendenti"
                value={metrics.amlPending}
                prefix={<ReconciliationOutlined />}
                valueStyle={
                  metrics.amlPending > 0 ? { color: "#cf1322" } : undefined
                }
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card
              size="small"
              hoverable
              onClick={() => navigate("/project-tasks")}
            >
              <Statistic
                title="Tasks"
                value={metrics.openTasks}
                prefix={<CheckSquareOutlined />}
                valueStyle={
                  metrics.openTasks > 0 ? { color: "#d46b08" } : undefined
                }
              />
            </Card>
          </Col>
        </Row>
      </div>

      {/* Carousel */}
      <FeatureCarousel cards={carouselCards} />

      {/* Search bar */}
      <UtilityBar
        searchValue={search}
        onSearchChange={setSearch}
        placeholder="Cerca policies, contenuti, progetti, AML..."
      />

      {/* Sectioned lists */}
      <SectionedList sections={sections} maxRows={20} />
    </div>
  );
};

export default Dashboard;
